import {
  type DevString,
  arrayEquals,
  dev,
  invariant,
  memoFn,
  tightJavaScriptify,
  weakMemoFn,
  when,
} from "@phosphor/utils";
import { ErrorBoundary } from "@sentry/react";
import { identity } from "effect";
import { equals } from "effect/Equal";
import { toJSON } from "effect/Inspectable";
import { type Atom, type PrimitiveAtom, atom, useSetAtom } from "jotai";
import { selectAtom } from "jotai/utils";
import type React from "react";
import { createContext, memo, useCallback, useContext, useId, useMemo, useState } from "react";
import { Button } from "#scripts/components/ui/button.tsx";
import { Ta } from "#scripts/components/ui/icons.tsx";
import { AtomValue } from "#scripts/lib/jotai/AtomValue.tsx";
import { useSelectAtom } from "#scripts/lib/jotai/useSelectAtom.ts";
import { DevPrint } from "./DevPrint.tsx";
import { getCollapseNonFavoritesAtom, getOnlyFavoritesAtom } from "./collapseNonFavoritesAtom.tsx";
import { type NameMetadata, type NameStatesAtom, getDevNameStatesAtom } from "./devNameStatesMapAtom.ts";
import { getFavoriteKeysOrderAtom } from "./favoriteKeysOrderAtom.tsx";
import { getColorFor } from "./getColorForString.tsx";
import { getObjectID } from "./getObjectID.tsx";
import { useClickByHoverToAvoidChangingFocus } from "./useClickByHoverToAvoidChangingFocus.tsx";

// Type stub for Color - matches @phosphor/server Color type
export type Color = { hue: number; chroma: number; lightness: number };

// Stub components - replace with actual UI components when available
const Badge = ({ children, className, variant, ...props }: any) => (
  <span className={className} {...props}>
    {children}
  </span>
);
const DevValueHover = ({ label, value }: any) => null;
const cn = (...classes: any[]) => classes.filter(Boolean).join(" ");

// Types
type Path = (string | number)[];

export interface RenderProps {
  name: string;
  data: unknown;
  /** Override color / color hash source */
  hashColor?: string | Color;
  /** Expected to be included before the name */
  showPath?: Path;
  /** Start collapsed */
  collapsed?: boolean;
  /** Show detailed view with higher depth limit */
  detailed?: boolean;
}

const DETAILED_MAX_DEPTH = 12;
const MAX_DEPTH = 6;

const DEFAULT_STRING_REGISTRY: StringRegistryItem<unknown>[] = [
  defineStringRenderer(
    (data) => {
      try {
        const parsed = JSON.parse(data);
        if (typeof parsed === "object" && parsed !== null) {
          return parsed;
        }
      } catch {}
      return undefined;
    },
    ({ data, showPath, forwardProps }) => <DevPrint {...forwardProps} value={data} title={showPath?.join(".")} />,
  ),
];

function uint8ArrayToUtf8String(uint8Array: Uint8Array) {
  return new TextDecoder("utf-8", { fatal: false }).decode(uint8Array);
}
const DEFAULT_REGISTRY: RegistryItem<UnknownObject>[] = [
  defineRenderer(
    (data) => data instanceof Map,
    ({ data, showPath, r }) => <r.Object name="Map" data={Object.fromEntries(data)} showPath={showPath} />,
  ),
  defineRenderer(
    (data) => data instanceof Set,
    ({ data, showPath, r }) => <r.Array name="Set" data={Array.from(data)} showPath={showPath} />,
  ),
  defineRenderer(
    (data) => data instanceof Uint8Array,
    ({ data, showPath, r }) => (
      <r.Object
        name="Uint8Array"
        data={{
          length: data.length,
          asString: uint8ArrayToUtf8String(data),
        }}
        showPath={showPath}
      />
    ),
  ),
  defineRenderer(
    (data) => data instanceof WeakMap,
    ({ data, showPath, r }) => <r.Placeholder name="WeakMap" showPath={showPath} />,
  ),
  defineRenderer(
    (data) => data instanceof WeakSet,
    ({ data, showPath, r }) => <r.Placeholder name="WeakSet" showPath={showPath} />,
  ),
  defineRenderer(
    (data) => data instanceof Error,
    ({ data, showPath, r }) => (
      <r.Object
        name={data.name || "Error"}
        data={{
          message: data.message,
          stack: data.stack,
          ...asObject(toJSON(data)),
        }}
        showPath={showPath}
      />
    ),
  ),
];

function asObject(data: unknown): UnknownObject {
  if (typeof data === "object" && data !== null) {
    return data;
  }
  return { value: data };
}

interface InternalRenderProps<T> extends RenderProps {
  data: T;
  /**
   * Instead of a single global "circularChecks",
   * we have a path-based approach: a Set of known IDs
   * in the current recursion chain.
   */
  seenIDs: Set<number>;
  /** Force show as detailed */
  detailed?: boolean;
  /** Depth of the current recursion */
  depth: number;
  /** Forward props to the component */
  forwardProps?: React$DOMDataAttributes;
}

type DevStateContainerProps = {
  icon: typeof Ta.Icon123;
  depth: number;
  showPath?: Path;
  toggleKey?: string;
  children?: React.ReactNode | ((event: React.MouseEvent) => void);
  /** for copying to clipboard, etc */
  rawValue: unknown;
  name?: string;
  nameChild?: React.ReactNode;
  inline?: boolean;
  isEmpty?: boolean;
  collapse?: boolean;
  hashColor?: string | Color;
  forwardProps?: React$DOMDataAttributes;
};

type KeyOf<T extends object> = keyof T extends string ? keyof T : never;
type CommaSeparatedKeys<T extends object> =
  | `${KeyOf<T>}`
  | `${KeyOf<T>},${KeyOf<T>}`
  | `${KeyOf<T>},${KeyOf<T>},${KeyOf<T>}`
  | `${KeyOf<T>},${KeyOf<T>},${KeyOf<T>},${string & {}}`;

export type RendererComponents = {
  Array: React.ComponentType<RenderProps & { data: unknown[] }>;
  Object: <T extends UnknownObject>(
    props: RenderProps & {
      data: T;
      /** Use omitKeys to exclude keys without needing to memo the data object. Separate by commas */
      commaSeparatedOmitKeys?: CommaSeparatedKeys<T>;
    },
  ) => JSX.Element;
  Unknown: React.ComponentType<RenderProps & { data: unknown }>;
  Placeholder: React.ComponentType<Omit<RenderProps, "data">>;
  Custom: React.ComponentType<Omit<DevStateContainerProps, "depth">>;
};

interface RendererProps<T> {
  data: T;
  showPath?: Path;
  detailed?: boolean;
  forwardProps: React$DOMDataAttributes;
  r: RendererComponents;
}

type Renderer<T> = (props: RendererProps<T>) => JSX.Element | null;
type UnknownObject = object & {};

export function defineRenderer<T extends UnknownObject>(
  test: (data: UnknownObject) => data is T,
  render: Renderer<T>,
): RegistryItem<any> {
  return { test, render };
}

export function defineStringRenderer<T>(
  parse: (data: string) => null | undefined | T,
  render: Renderer<T>,
): StringRegistryItem<any> {
  return { parse, render };
}

export interface RegistryItem<T extends UnknownObject> {
  test: (data: UnknownObject) => data is T;
  render: Renderer<T>;
}

export interface StringRegistryItem<T> {
  parse: (data: string) => null | undefined | T;
  render: Renderer<T>;
}

interface RenderRegistryContextValue {
  objects: RegistryItem<UnknownObject>[];
  strings: StringRegistryItem<unknown>[];
  orderedKeys?: string[];
}

const RenderRegistryContext = createContext<RenderRegistryContextValue>({
  objects: DEFAULT_REGISTRY,
  strings: DEFAULT_STRING_REGISTRY,
  orderedKeys: undefined,
});

export const ProvideDevStateRenderers = ({
  children,
  registry = [],
  stringRegistry = [],
  orderedKeys = [],
}: React.PropsWithChildren<{
  registry?: RegistryItem<UnknownObject>[];
  stringRegistry?: StringRegistryItem<unknown>[];
  orderedKeys?: string[];
}>) => {
  const value = useMemo(
    (): RenderRegistryContextValue => ({
      objects: [...DEFAULT_REGISTRY, ...registry],
      strings: [...DEFAULT_STRING_REGISTRY, ...stringRegistry],
      orderedKeys,
    }),
    [registry, stringRegistry, orderedKeys],
  );

  return <RenderRegistryContext.Provider value={value}>{children}</RenderRegistryContext.Provider>;
};

const useRenderRegistry = () => useContext(RenderRegistryContext);
const RenderHook = ({ hook }: { hook: () => React.ReactElement | null }) => hook();

const getR = weakMemoFn((seenIDs: Set<number>) =>
  weakMemoFn((forwardProps: React$DOMDataAttributes) =>
    memoFn((depth: number): RendererComponents => {
      const commonProps = { seenIDs, forwardProps, depth };
      return {
        Array: (props) => <DevStateArray {...commonProps} {...props} />,
        Object: (props) => <DevStateObject {...commonProps} {...props} />,
        Unknown: (props) => <DevStateInternal {...commonProps} {...props} />,
        Custom: (props) => <DevStateContainer {...commonProps} {...props} />,
        Placeholder: (props) => (
          <Badge {...forwardProps} className="text-xs" variant="outline">
            <Ta.IconBox data-loc="phosphor-blog/scripts/lib/dev/DevState.tsx:270:25" data-name="Ta.IconBox" className="w-4 h-4 -ml-1 mr-1" />
            <MaybeShowPath path={props.showPath} hashColor={props.hashColor} />
            {props.name}
          </Badge>
        ),
      };
    }),
  ),
);

/**
 * We define a context that holds the devNameStates Map, plus the collapseNonFavorites
 * and onlyFavorites booleans, for the given devStateName. This allows child
 * components in the tree to read or update them easily.
 */

interface InternalDisplayContextValue {
  devNameStatesAtom: NameStatesAtom;
  favoriteKeysOrderAtom: Atom<(obj: object) => string[]>;
  collapseNonFavoritesAtom: PrimitiveAtom<boolean>;
  onlyFavoritesAtom: PrimitiveAtom<boolean>;
}

const InternalDisplayContext = createContext<InternalDisplayContextValue | null>(null);

function useInternalDisplay(devStateName: string, orderedKeys: string[]): InternalDisplayContextValue {
  const { orderedKeys: registryOrderedKeys } = useRenderRegistry();
  // get the derived atoms for this devStateName
  const devNameStatesAtom = getDevNameStatesAtom(devStateName);
  const collapseNonFavoritesAtom = getCollapseNonFavoritesAtom(devStateName);
  const onlyFavoritesAtom = getOnlyFavoritesAtom(devStateName);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const favoriteKeysOrderAtom = useMemo(() => {
    let useOrderedKeys: string[] | undefined = [...orderedKeys, ...(registryOrderedKeys ?? [])];
    useOrderedKeys = useOrderedKeys.length === 0 ? undefined : useOrderedKeys;
    return selectAtom(getFavoriteKeysOrderAtom(devStateName), (fn) => (obj: object) => fn(obj, useOrderedKeys));
  }, [devStateName, orderedKeys?.join("/"), registryOrderedKeys?.join("/")]);

  return {
    devNameStatesAtom,
    collapseNonFavoritesAtom,
    onlyFavoritesAtom,
    favoriteKeysOrderAtom,
  };
}

/**
 * DevStateContainer is the top-level container for each row or item
 * in the DevState. It includes the "favorite" button, the name, expansion/collapse, etc.
 */
const DevStateContainer = memo(
  ({
    icon: Icon,
    children,
    rawValue,
    name,
    nameChild,
    showPath,
    isEmpty,
    hashColor,
    depth,
    collapse,
    inline,
    toggleKey = showPath?.[0]?.toString() ?? name,
    forwardProps = DEFAULT_FORWARD_PROPS,
  }: DevStateContainerProps) => {
    const { devNameStatesAtom, collapseNonFavoritesAtom, onlyFavoritesAtom } = useInternalDisplayContext();

    const id = useId();
    const nameStateKey = toggleKey ?? id;
    const canBeFavorite = showPath && depth === showPath.length;

    const nameStateAtom = useMemo(
      () =>
        atom((get) => {
          const collapseNonFavorites = get(collapseNonFavoritesAtom);
          const onlyFavorites = get(onlyFavoritesAtom);
          const devNameStates = get(devNameStatesAtom);
          const specifiedNameState = nameStatesGet(devNameStates, nameStateKey);
          // If depth < MAX_DEPTH, let's not collapse by default
          const overMaxCollapsed = depth < MAX_DEPTH ? null : true;
          const defaultCollapse = collapse || (collapseNonFavorites && depth > 1);

          const collapsed = specifiedNameState?.collapsed ?? overMaxCollapsed ?? defaultCollapse;
          const favorite = specifiedNameState?.favorite ?? false;
          const visibility =
            !favorite && collapseNonFavorites && canBeFavorite ? (onlyFavorites ? "hide" : "dim") : "show";
          return {
            collapsed,
            favorite,
            visibility,
          };
        }),
      [canBeFavorite, devNameStatesAtom, nameStateKey, depth, collapse, collapseNonFavoritesAtom, onlyFavoritesAtom],
    );
    // useDevChangedDeps(dev`nameStateAtom`.ctx({ loc: "phosphor-blog/scripts/lib/dev/DevState.tsx:366:26" }), { canBeFavorite, devNameStatesAtom, nameStateKey, depth, collapse, collapseNonFavoritesAtom, onlyFavoritesAtom })

    // Determine nameState
    const nameState = useSelectAtom(nameStateAtom, identity, [], equals);

    const setDevNameStates = useSetAtom(devNameStatesAtom);

    const canToggleExpand = nameStateKey && !inline;
    const toggleExpanded = useCallback(() => {
      if (!canToggleExpand) return;
      setDevNameStates([nameStateKey, { collapsed: !nameState.collapsed }]);
    }, [nameState.collapsed, nameStateKey, setDevNameStates, canToggleExpand]);

    const pathJoined = useMemo(() => showPath?.join("."), [showPath]);
    const color = useMemo(() => {
      const colorSource = hashColor ?? pathJoined;
      return colorSource ? getColorFor(colorSource) : undefined;
    }, [hashColor, pathJoined]);
    const clickChild = typeof children === "function";
    const style = useMemo(() => {
      if (!color) return undefined;
      return clickChild
        ? {
            color: color.textPrimary.color,
            backgroundColor: color.container.backgroundColor,
            borderColor: color.container.borderColor,
          }
        : { color: color.textPrimary.color };
    }, [color, clickChild]);

    const DisplayIcon = clickChild ? Ta.IconPlayerPlay : !inline && nameState.collapsed ? Ta.IconChevronRight : Icon;

    const [buttonElt, setButtonElt] = useState<HTMLButtonElement | null>(null);
    const handleClick = useClickByHoverToAvoidChangingFocus(
      buttonElt,
      children ? (clickChild ? children : !nameStateKey || inline ? undefined : toggleExpanded) : undefined,
    );

    const showContent = !nameState.collapsed;
    if (nameState.visibility === "hide") return null;

    return (
      <div
        {...forwardProps}
        className={cn("text-xs", canBeFavorite && nameState.visibility === "dim" && "opacity-50")}
        data-depth={depth}
      >
        <div {...forwardProps} className="flex items-center gap-1 flex-wrap pl-4 relative">
          <Button
            {...forwardProps}
            className={cn("-ml-4 text-xs", !handleClick && "cursor-default", nameState.collapsed && "h-5 py-0")}
            variant="ghost"
            size="xs"
            style={style}
            ref={setButtonElt}
            onClick={handleClick}
          >
            <DisplayIcon data-loc="phosphor-blog/scripts/lib/dev/DevState.tsx:423:26" data-name="DisplayIcon" className={cn("w-4 h-4 -ml-1 mr-1", isEmpty && "opacity-30")} />
            <MaybeShowPath
              path={showPath}
              showBackground={clickChild || !isEmpty}
              hashColor={hashColor}
              trailingSlash={!isEmpty && !clickChild}
            />
            {!nameChild && name && <span data-loc="phosphor-blog/scripts/lib/dev/DevState.tsx:430:42" data-name="span" className="mr-1">{name}</span>}
          </Button>
          {rawValue && typeof rawValue === "object" ? <DevValueHover label={name ?? "Value"} value={rawValue} /> : null}
          {nameChild}
          {nameStateKey && canBeFavorite && (
            <FavoriteButton nameStateKey={nameStateKey} isCollapsed={nameState.collapsed} />
          )}
          {!clickChild && inline && children}
        </div>
        {!clickChild && !inline && showContent && children}
      </div>
    );
  },
);
DevStateContainer.displayName = "DevStateContainer";

const nameStatesGet = (map: Map<string, NameMetadata | undefined>, key: string) => {
  return map.get(key);
};

/**
 * Button for toggling favorites
 */
const FavoriteButton = memo(({ nameStateKey, isCollapsed }: { nameStateKey: string; isCollapsed?: boolean }) => {
  const { devNameStatesAtom } = useInternalDisplayContext();

  const isFavoriteAtom = useMemo(
    () => atom((get) => get(devNameStatesAtom).get(nameStateKey)?.favorite || false),
    [devNameStatesAtom, nameStateKey],
  );

  const setFavorite = useSetAtom(devNameStatesAtom);
  const toggleFavorite = useCallback(() => {
    setFavorite([nameStateKey, (prev) => ({ favorite: !prev?.favorite })]);
  }, [setFavorite, nameStateKey]);

  return (
    <Button
      data-loc="phosphor-blog/scripts/lib/dev/DevState.tsx:468:7" data-name="Button" className={cn("text-xs p-1 absolute -left-5 top-0", isCollapsed && "py-0 h-5")}
      variant="ghost"
      size="xs"
      onClick={toggleFavorite}
      title={`Toggle Favorite "${nameStateKey}"`}
    >
      <AtomValue atom={isFavoriteAtom}>
        {(isFavorite) =>
          isFavorite ? <Ta.IconStarFilled data-loc="phosphor-blog/scripts/lib/dev/DevState.tsx:476:43" data-name="Ta.IconStarFilled" className="w-3 h-3 text-yellow-500" /> : <Ta.IconStar data-loc="phosphor-blog/scripts/lib/dev/DevState.tsx:476:89" data-name="Ta.IconStar" className="w-3 h-3 opacity-50" />
        }
      </AtomValue>
    </Button>
  );
});
FavoriteButton.displayName = "FavoriteButton";

const ProvideInternalDisplayContext = memo(
  ({
    devStateName,
    orderedKeys = [],
    children,
  }: {
    devStateName: string;
    orderedKeys?: string[];
    children: React.ReactNode;
  }) => {
    const value = useInternalDisplay(devStateName, orderedKeys);
    return <InternalDisplayContext.Provider value={value}>{children}</InternalDisplayContext.Provider>;
  },
);

ProvideInternalDisplayContext.displayName = "ProvideInternalDisplayContext";

function useInternalDisplayContext() {
  const ctx = useContext(InternalDisplayContext);
  invariant(ctx, "useInternalDisplayContext used outside of InternalDisplayContext!");
  return ctx;
}

interface DevStateProps extends RenderProps {
  /**
   * DevStateProps extends the usual RenderProps but also uses the name in a unique way,
   * which indicates which "scope" or "instance" of DevState we're using, so that
   * local storage keys and favorites are kept separate per DevState name.
   */
  name: string;
}

/**
 * Top-level DevState component. We pass a devStateName (default "default"),
 * then wrap the children in ProvideDevStateContext so deeper components can read
 * the name states & collapse booleans for this scope.
 */
export const DevState = memo(
  ({
    name,
    data,
    detailed,
    collapsed,
    hashColor,
    showPath,
    ...forwardProps
  }: DevStateProps & React$DOMDataAttributes) => {
    // For the top-level object, we start with a fresh seenIDs for this entire recursion
    const seenIDs = useMemo(() => new Set<number>(), []);
    return (
      <ProvideInternalDisplayContext devStateName={name}>
        <DevStateInternal
          forwardProps={forwardProps}
          name={name}
          seenIDs={seenIDs}
          depth={0}
          data={data}
          detailed={detailed}
          collapsed={collapsed}
          hashColor={hashColor}
          showPath={showPath}
        />
      </ProvideInternalDisplayContext>
    );
  },
);
DevState.displayName = "DevState";

function checkCircular(data: unknown, seenIDs: Set<number>): boolean {
  if (typeof data !== "object" || data === null) {
    return false;
  }
  const id = getObjectID(data);
  if (seenIDs.has(id)) {
    // Already in path => circular
    return true;
  }
  seenIDs.add(id);
  return false;
}

const DEFAULT_FORWARD_PROPS = {} as React$DOMDataAttributes;

/**
 * The core recursive DevStateInternal component
 */
const DevStateInternal = memo(
  ({
    data,
    name,
    showPath,
    seenIDs,
    detailed,
    depth,
    collapsed,
    hashColor,
    forwardProps = DEFAULT_FORWARD_PROPS,
  }: InternalRenderProps<unknown>) => {
    const { objects: registry, strings: stringRegistry } = useRenderRegistry();

    if (data == null) {
      return (
        <DevStateContainer
          rawValue={data}
          forwardProps={forwardProps}
          icon={Ta.IconQuestionMark}
          depth={depth}
          showPath={showPath}
          name={name}
          inline
          hashColor={hashColor}
        >
          <code data-loc="phosphor-blog/scripts/lib/dev/DevState.tsx:596:17" data-name="code" className="text-gray-700 bg-gray-100 rounded-md px-1" title={name}>
            {String(data)}
          </code>
        </DevStateContainer>
      );
    }

    // String registry check
    if (typeof data === "string") {
      for (const { parse, render } of stringRegistry) {
        const parsed = parse(data);
        if (parsed != null) {
          return (
            <ErrorBoundary
              fallback={(error) => (
                <span data-loc="phosphor-blog/scripts/lib/dev/DevState.tsx:611:23" data-name="span" className="text-gray-500">
                  Error rendering {name}
                  <pre data-loc="phosphor-blog/scripts/lib/dev/DevState.tsx:613:24" data-name="pre" className="text-red-500">{tightJavaScriptify(error)}</pre>
                </span>
              )}
            >
              <RenderHook
                hook={() =>
                  render({
                    data: parsed,
                    showPath,
                    r: getR(seenIDs)(forwardProps)(depth),
                    detailed,
                    forwardProps,
                  })
                }
              />
            </ErrorBoundary>
          );
        }
      }
    }

    // Primitives
    if (typeof data === "string" || typeof data === "number" || typeof data === "boolean" || typeof data === "symbol") {
      const Icon =
        typeof data === "string"
          ? Ta.IconAbc
          : typeof data === "number"
            ? Ta.IconNumber
            : typeof data === "boolean"
              ? data
                ? Ta.IconCheckbox
                : Ta.IconSquare
              : Ta.IconQuestionMark;
      const asStr = String(data);
      return (
        <DevStateContainer
          rawValue={data}
          icon={Icon}
          showPath={showPath}
          name={name}
          hashColor={typeof data}
          depth={depth}
          forwardProps={forwardProps}
          inline
        >
          {asStr ? (
            <code data-loc="phosphor-blog/scripts/lib/dev/DevState.tsx:659:19" data-name="code" className="text-gray-700 bg-gray-100 rounded-md px-1 select-text whitespace-pre-wrap" title={name}>
              {String(data)}
            </code>
          ) : (
            <em data-loc="phosphor-blog/scripts/lib/dev/DevState.tsx:663:17" data-name="em" className="text-orange-900 text-xs opacity-75 font-medium">{JSON.stringify(data)}</em>
          )}
        </DevStateContainer>
      );
    }

    // arrays
    const childSeenIDs = new Set(seenIDs);
    const isCircular = checkCircular(data, childSeenIDs);
    if (isCircular) {
      return (
        <Badge {...forwardProps} className="text-xs" variant="destructive">
          Circular
        </Badge>
      );
    }

    // custom renderers
    for (const { test, render } of registry) {
      if (test(data as UnknownObject)) {
        return (
          <ErrorBoundary
            fallback={(error) => (
              <span data-loc="phosphor-blog/scripts/lib/dev/DevState.tsx:686:21" data-name="span" className="text-gray-500">
                Error rendering {name}
                <pre data-loc="phosphor-blog/scripts/lib/dev/DevState.tsx:688:22" data-name="pre" className="text-red-500">{tightJavaScriptify(error)}</pre>
              </span>
            )}
          >
            <RenderHook
              hook={() =>
                render({
                  data: data as any,
                  showPath,
                  r: getR(seenIDs)(forwardProps)(depth),
                  detailed,
                  forwardProps,
                })
              }
            />
          </ErrorBoundary>
        );
      }
    }

    // function
    if (typeof data === "function") {
      const asFn = data as (reason: DevString, event: React.MouseEvent) => unknown;
      return (
        <DevStateContainer
          rawValue={data}
          icon={Ta.IconPlayerPlay}
          showPath={showPath}
          name={name}
          depth={depth}
          forwardProps={forwardProps}
          children={(event) => asFn(dev`Clicked from DevState`.ctx({ loc: "phosphor-blog/scripts/lib/dev/DevState.tsx:719:37" }), event)}
        />
      );
    }

    // array
    if (Array.isArray(data)) {
      return (
        <DevStateArray
          data={data}
          name={name || `Array`}
          showPath={showPath}
          seenIDs={childSeenIDs}
          detailed={detailed}
          depth={depth}
          forwardProps={forwardProps}
        />
      );
    }

    // Objects
    if (typeof data === "object") {
      const constructorName = getObjectConstructor(data);
      return (
        <DevStateObject
          data={data}
          name={[name, constructorName !== "Object" && `[object ${constructorName}]`].filter(Boolean).join(" ")}
          showPath={showPath}
          seenIDs={childSeenIDs}
          detailed={detailed}
          depth={depth}
          forwardProps={forwardProps}
        />
      );
    }

    return (
      <span {...forwardProps} className="text-gray-500">
        Unsupported type: {typeof data}
      </span>
    );
  },
);
DevStateInternal.displayName = "DevStateInternal";

const MAX_ITEMS_SHOWN_BEFORE_HIDE = 10;

/**
 * DevStateArray
 */
const DevStateArray = memo(
  ({
    data,
    name,
    showPath,
    seenIDs,
    detailed,
    depth,
    collapsed,
    hashColor,
    forwardProps = DEFAULT_FORWARD_PROPS,
  }: InternalRenderProps<unknown[]>) => {
    const isEmpty = data.length === 0;

    const isTooMany = data.length > MAX_ITEMS_SHOWN_BEFORE_HIDE;
    const [isOpen, setIsOpen] = useState(false);
    const toggleOpen = useCallback(() => setIsOpen((prev) => !prev), []);
    const isOpenEffective = !isTooMany || isOpen;

    const itemsToShow = useMemo(
      () => (isOpenEffective ? data : data.slice(0, MAX_ITEMS_SHOWN_BEFORE_HIDE)),
      [isOpenEffective, data],
    );

    if (data.length === 1) {
      return (
        <DevStateInternal
          name={name}
          data={data[0]}
          showPath={[...(showPath ?? []), "[0]"]}
          seenIDs={seenIDs}
          detailed={detailed}
          depth={depth + 1}
          forwardProps={forwardProps}
          collapsed={collapsed}
        />
      );
    }

    return (
      <DevStateContainer
        rawValue={data}
        icon={Ta.IconChevronDown}
        showPath={showPath}
        name={`${name} (${data.length})`}
        isEmpty={isEmpty}
        depth={depth}
        forwardProps={forwardProps}
        collapse={collapsed}
        hashColor={hashColor}
      >
        {isEmpty && <em data-loc="phosphor-blog/scripts/lib/dev/DevState.tsx:820:25" data-name="em" className="text-gray-500 text-xs mx-2 mb-2 -mt-1 inline-block">Empty</em>}
        <ul data-loc="phosphor-blog/scripts/lib/dev/DevState.tsx:821:13" data-name="ul" className="list-none pl-4 border-l border-gray-200">
          {itemsToShow.map((item, index) => {
            const childSeenIDs = new Set(seenIDs);
            return (
              <li key={index} data-loc="phosphor-blog/scripts/lib/dev/DevState.tsx:825:31" data-name="li" className="py-0.5">
                <DevStateInternal
                  name=""
                  data={item}
                  showPath={[index]}
                  seenIDs={childSeenIDs}
                  detailed={detailed}
                  depth={depth + 1}
                  forwardProps={DEFAULT_FORWARD_PROPS}
                  collapsed={collapsed}
                />
              </li>
            );
          })}
          {isTooMany && (
            <li data-loc="phosphor-blog/scripts/lib/dev/DevState.tsx:840:17" data-name="li">
              <button data-loc="phosphor-blog/scripts/lib/dev/DevState.tsx:841:23" data-name="button" className="text-gray-500 text-xs mx-2 mb-2 -mt-1 inline-block hover:text-gray-700" onClick={toggleOpen}>
                {isOpenEffective ? "Show first " + MAX_ITEMS_SHOWN_BEFORE_HIDE : "Show all " + data.length}
              </button>
            </li>
          )}
        </ul>
      </DevStateContainer>
    );
  },
);
DevStateArray.displayName = "DevStateArray";

/**
 * DevStateObject
 */
const DevStateObject = memo(
  ({
    data,
    name,
    showPath,
    seenIDs,
    detailed,
    depth,
    commaSeparatedOmitKeys,
    collapsed,
    hashColor,
    forwardProps = DEFAULT_FORWARD_PROPS,
  }: InternalRenderProps<UnknownObject> & { commaSeparatedOmitKeys?: string }) => {
    const { favoriteKeysOrderAtom } = useInternalDisplayContext();
    const omitKeysSet = useMemo(() => new Set(commaSeparatedOmitKeys?.split(",")), [commaSeparatedOmitKeys]);
    const keys = useSelectAtom(
      favoriteKeysOrderAtom,
      (fn) => fn(data).filter((a) => !omitKeysSet.has(a)),
      [data, omitKeysSet],
      arrayEquals,
    );
    // useDevChangedDeps(dev`DevStateObject`.ctx({ loc: "phosphor-blog/scripts/lib/dev/DevState.tsx:877:26" }), { favoriteKeysOrderAtom, data, keys });

    if (keys.length === 1) {
      return (
        <DevStateInternal
          name={name}
          data={(data as any)[keys[0]!]}
          showPath={[...(showPath ?? []), keys[0]!]}
          seenIDs={seenIDs}
          detailed={detailed}
          depth={depth + 1}
          forwardProps={forwardProps}
          collapsed={collapsed}
          hashColor={hashColor}
        />
      );
    }

    const isEmpty = keys.length === 0;

    return (
      <div data-loc="phosphor-blog/scripts/lib/dev/DevState.tsx:898:12" data-name="div" className={cn(depth > 0 && "border-l border-gray-200")}>
        <DevStateContainer
          rawValue={data}
          icon={Ta.IconChevronDown}
          showPath={showPath}
          name={name}
          isEmpty={isEmpty}
          depth={depth}
          collapse={collapsed}
          inline={isEmpty}
          hashColor={hashColor}
          forwardProps={forwardProps}
        >
          {isEmpty && <em data-loc="phosphor-blog/scripts/lib/dev/DevState.tsx:911:27" data-name="em" className="text-gray-500 text-xs">Empty</em>}
          {!isEmpty && (
            <div {...forwardProps} className="pl-4 flex flex-col items-start">
              {keys.map((key) => (
                <DevStateInternal
                  key={key}
                  name=""
                  data={(data as any)[key]}
                  showPath={[key]}
                  seenIDs={seenIDs}
                  detailed={detailed}
                  depth={depth + 1}
                  forwardProps={DEFAULT_FORWARD_PROPS}
                />
              ))}
            </div>
          )}
        </DevStateContainer>
      </div>
    );
  },
);
DevStateObject.displayName = "DevStateObject";

/**
 * MaybeShowPath
 */
const MaybeShowPath = ({
  path,
  hashColor,
  showBackground,
  trailingSlash,
}: {
  path?: Path;
  hashColor?: string | Color;
  showBackground?: boolean;
  trailingSlash?: boolean;
}) => {
  if (!path) return null;
  return (
    <RenderPath
      path={path.join(".")}
      hashColor={hashColor ?? String(path?.[0])}
      showBackground={showBackground}
      trailingSlash={trailingSlash}
    />
  );
};

const RenderPath = memo(
  ({
    path,
    hashColor,
    showBackground,
    trailingSlash = true,
  }: {
    path: string;
    hashColor?: string | Color;
    showBackground?: boolean;
    trailingSlash?: boolean;
  }) => {
    if (!path) return null;
    const color = when(hashColor, getColorFor);
    return (
      <span
        data-loc="phosphor-blog/scripts/lib/dev/DevState.tsx:976:9" data-name="span" className="font-semibold"
        style={
          color && {
            color: color.textPrimary.color,
            backgroundColor: showBackground ? color.container.backgroundColor : "transparent",
          }
        }
      >
        {path}
        {trailingSlash ? " / " : ""}&nbsp;
      </span>
    );
  },
);
RenderPath.displayName = "RenderPath";

const getObjectConstructor = (data: UnknownObject) => {
  try {
    return Object.getPrototypeOf(data).constructor.name;
  } catch {
    return "Unknown";
  }
};
